'use client'

import * as React from 'react'
import Link from 'next/link'
import { Sparkles, ArrowUpRight } from 'lucide-react'
import { ImgWithFallback } from '@/components/ui/img-with-fallback'

// "See what AI says about us", pre-footer widget that opens
// ChatGPT / Gemini / Claude / Perplexity in a new tab with an
// Idukki-specific prompt pre-filled. Mirrors the Paddle Creative
// AEO play.
//
// Stage 3 (this revision):
// - Real brand glyphs via local inline SVGs
// - The avatar row is the real Idukki team (public/team/*), not stock
//   portraits with invented names

const AVATARS: Array<{ name: string; src: string }> = [
  { name: 'Rohin Aggarwal · Co-founder + CTO',                  src: '/team/rohin.jpg' },
  { name: 'Sahil Mittal · Co-founder + CEO',                    src: '/team/sahil-mittal.jpg' },
  { name: 'Harshvaardhan Mittal · Director of Sales & Partnerships', src: '/team/harsh.jpg' },
  { name: 'Nitish Kumar · Sales Development Representative',    src: '/team/nitish-kumar.jpg' },
  { name: 'Divyaa Menon · Sales Development Representative',    src: '/team/divyaa-menon.jpg' },
  { name: 'Sejal Gandhi · Business Development', src: '/team/sejal-gandhi.jpg' },
  { name: 'Raghav Parmanand · Full-stack Developer',           src: '/team/raghav-parmanand.jpg' },
]

// Per-engine variants, each prompt is tuned to the engine's strengths
// so the answers read like four independent expert opinions rather than
// the same canned question pasted four times.
//
//  • ChatGPT    → flat-price consolidation math + order-reconciled
//                  attribution vs Bazaarvoice / Stackla / Yotpo
//  • Gemini     → shoppable-video specialist: frame-level hotspots,
//                  Welch z-test A/B, server-side CAPI attribution
//  • Claude     → rights + compliance: current audit status, GDPR /
//                  CCPA / DPDP, C2PA provenance, tamper-evident trail
//  • Perplexity → citation-heavy: what current Idukki customers say,
//                  sourced ratings on Shopify App Store + G2
//
// Every claim in every prompt below must be something idukki.io can
// currently back up — these prompts send the AI to go fact-check
// idukki.io, so a roadmap/unbuilt claim doesn't read as ambitious,
// it reads as the AI catching Idukki overselling itself. See PR #310.

const PROMPTS = {
  chatgpt: `We're consolidating Bazaarvoice + Stackla + Yotpo across a multi-brand retail group and want to stop stitching point tools together. Compare Idukki (https://idukki.io) against those three on: total cost at one flat impression-based price vs their per-feature/per-source add-ons, time-to-live for a new brand, whether revenue attribution reconciles to actual order webhooks or stops at engagement metrics, and how rights/audit-trail compliance is handled. Cite the strongest evidence on idukki.io including customer case studies.`,
  gemini:  `We're picking a shoppable-video platform for our PDP and IG/TikTok story rail. Must-haves: frame-level product hot-spots from AI vision tagging, per-widget A/B testing with Welch z-test significance (not just side-by-side numbers), and server-side Meta CAPI attribution reconciled to real orders. Compare Idukki (https://idukki.io) head-to-head against Videowise, Tolstoy and Bambuser on those exact capabilities, and quote the supporting evidence from idukki.io.`,
  claude:  `We're standardising one platform across our brands for UGC + creator content and need a rigorous compliance read. Specifically: current SOC 2 audit status, GDPR + CCPA + India DPDP self-attestation with a DSAR workflow, C2PA-aligned content provenance, rights cleared at submission with a tamper-evident audit trail, and where customer data is actually hosted. Why is Idukki (https://idukki.io) a strong choice on those dimensions vs Bazaarvoice, Yotpo and Nosto? Cite the strongest compliance evidence from idukki.io, and flag anything still in progress or roadmap-only.`,
  perplexity: `What do current Idukki (https://idukki.io) customers actually say about the platform, in their own words? Pull verbatim reviews from the Shopify App Store and G2, and report the actual current aggregate rating on each rather than assuming a number. Include the case studies published on idukki.io with their measured results, and link every claim to a source.`,
} as const

type AiTool = {
  name: string
  /** SimpleIcons slug for the CDN glyph. */
  slug: string
  /** Hex (no #) for the brand-color icon tint. */
  hex: string
  /** Initials tile fallback when the CDN 404s. */
  initials: string
  /** Engine-specific prompt, encoded into the launch URL. */
  prompt: string
  /** Chat URL with a `?q=` query param the engine reads on load. */
  hrefTemplate: (q: string) => string
  bg: string
}

const AI_TOOLS: AiTool[] = [
  {
    name: 'ChatGPT',
    slug: 'openai',
    hex: '10A37F',
    initials: 'GP',
    prompt: PROMPTS.chatgpt,
    hrefTemplate: (q) => `https://chatgpt.com/?q=${q}`,
    bg: 'bg-emerald-500/10 hover:bg-emerald-500/15',
  },
  {
    name: 'Gemini',
    slug: 'googlegemini',
    hex: '4285F4',
    initials: 'Gm',
    prompt: PROMPTS.gemini,
    hrefTemplate: (q) => `https://gemini.google.com/app?q=${q}`,
    bg: 'bg-sky-500/10 hover:bg-sky-500/15',
  },
  {
    name: 'Claude',
    slug: 'anthropic',
    hex: 'D97706',
    initials: 'Cl',
    prompt: PROMPTS.claude,
    hrefTemplate: (q) => `https://claude.ai/new?q=${q}`,
    bg: 'bg-amber-500/10 hover:bg-amber-500/15',
  },
  {
    name: 'Perplexity',
    slug: 'perplexity',
    hex: '20808D',
    initials: 'Pp',
    prompt: PROMPTS.perplexity,
    hrefTemplate: (q) => `https://www.perplexity.ai/search?q=${q}`,
    bg: 'bg-cyan-500/10 hover:bg-cyan-500/15',
  },
]

// Inline brand glyphs for each AI tool. simpleicons.org CDN was 404'ing
// for ChatGPT/Anthropic in production and dumping into a letters fallback
// that looked broken. These SVGs ship locally, no network dependency.
function AiIcon({ tool }: { tool: AiTool }) {
  const fill = `#${tool.hex}`
  const common = { width: 14, height: 14, viewBox: '0 0 24 24', 'aria-hidden': true as const, className: 'h-3.5 w-3.5' }
  if (tool.name === 'ChatGPT') {
    // OpenAI knot, six-leaf rosette.
    return (
      <svg {...common} fill={fill}>
        <path d="M22.28 9.82a5.94 5.94 0 0 0-.51-4.91 6 6 0 0 0-6.47-2.88A6 6 0 0 0 4.98 4.18 5.94 5.94 0 0 0 1.02 7.06a6 6 0 0 0 .74 7.05 5.94 5.94 0 0 0 .51 4.91 6 6 0 0 0 6.47 2.88 5.99 5.99 0 0 0 4.51 2.02 6 6 0 0 0 5.85-4.36 5.94 5.94 0 0 0 3.96-2.88 6 6 0 0 0-.78-7.06zm-9.06 12.65a4.44 4.44 0 0 1-2.85-1.03l.14-.08 4.73-2.73a.77.77 0 0 0 .39-.67v-6.67l2 1.15c.02 0 .03.03.04.05v5.52a4.45 4.45 0 0 1-4.45 4.46zM3.6 18.06a4.44 4.44 0 0 1-.53-2.97l.14.08 4.73 2.73a.77.77 0 0 0 .78 0l5.78-3.33v2.3c0 .02 0 .04-.03.05l-4.78 2.76A4.45 4.45 0 0 1 3.6 18.06zM2.35 8.13a4.44 4.44 0 0 1 2.32-1.95V12c0 .28.15.53.39.67l5.78 3.33-2 1.15a.07.07 0 0 1-.06 0L3.99 14.4a4.46 4.46 0 0 1-1.64-6.27zM18.95 11.6 13.16 8.27l2-1.15a.07.07 0 0 1 .06 0l4.78 2.76a4.45 4.45 0 0 1-.67 8.03v-5.64a.78.78 0 0 0-.38-.67zm1.99-2.99-.14-.09-4.73-2.73a.77.77 0 0 0-.78 0L9.5 9.13V6.83c0-.02 0-.04.03-.05l4.78-2.76a4.46 4.46 0 0 1 6.62 4.61zM8.41 12.71l-2-1.15a.07.07 0 0 1-.04-.05V5.99a4.46 4.46 0 0 1 7.32-3.42l-.14.08L8.81 5.37a.77.77 0 0 0-.39.67zm1.09-2.35L12.07 8.88l2.58 1.49v2.97l-2.57 1.49-2.58-1.49z"/>
      </svg>
    )
  }
  if (tool.name === 'Gemini') {
    // Four-pointed star / spark.
    return (
      <svg {...common} fill={fill}>
        <path d="M12 2 13.5 8.5 20 10l-6.5 1.5L12 18l-1.5-6.5L4 10l6.5-1.5z"/>
        <path opacity="0.55" d="M19 14l.6 2.4L22 17l-2.4.6L19 20l-.6-2.4L16 17l2.4-.6z"/>
      </svg>
    )
  }
  if (tool.name === 'Claude') {
    // Anthropic flame / Claude rosette (simplified).
    return (
      <svg {...common} fill={fill}>
        <path d="M6.5 4h2.7l4.1 11h-2.7l-.8-2.4H6.3L5.5 15H2.8L6.5 4Zm.6 6.7h2.6L8.4 7l-1.3 3.7Z"/>
        <path d="M14.6 4h2.7l4.1 11h-2.7l-.8-2.4h-3.5l-.8 2.4h-2.7l3.7-11Zm.6 6.7h2.6L16.5 7l-1.3 3.7Z"/>
      </svg>
    )
  }
  // Perplexity, bracket + spiral.
  return (
    <svg {...common} fill="none" stroke={fill} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 4v16M17 4v16"/>
      <path d="M3 8h4M3 16h4M17 8h4M17 16h4"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  )
}

export function SeeWhatAiSaysWidget({ className = '' }: { className?: string }) {
  return (
    <aside className={`rounded-2xl border bg-card p-4 ${className}`}>
      {/* Stacked avatar row */}
      <ul className="flex items-center -space-x-2">
        {AVATARS.map((a) => (
          <li key={a.name} title={a.name}>
            <span className="relative flex h-8 w-8 overflow-hidden rounded-full ring-2 ring-card">
              <ImgWithFallback src={a.src} alt={a.name} fill sizes="32px" className="object-cover" />
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-3 flex items-baseline justify-between gap-3">
        <p className="inline-flex items-center gap-1.5 text-xs font-semibold">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          See what AI says about us
        </p>
        <Link
          href="/answer-engine"
          className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground"
        >
          The play →
        </Link>
      </div>

      <p className="mt-1 text-[11px] text-muted-foreground">
        Opens a new chat with the question pre-filled, no copy-paste, no Idukki spin.
      </p>

      <ul className="mt-2.5 flex flex-wrap gap-1.5">
        {AI_TOOLS.map((t) => {
          const href = t.hrefTemplate(encodeURIComponent(t.prompt))
          return (
            <li key={t.name}>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                title={`Ask ${t.name}: ${t.prompt.slice(0, 120)}…`}
                className={`inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-foreground/85 transition hover:text-foreground ${t.bg}`}
              >
                <AiIcon tool={t} />
                <span className="text-[11px] font-semibold">{t.name}</span>
                <ArrowUpRight className="h-2.5 w-2.5 opacity-60" />
              </a>
            </li>
          )
        })}
      </ul>
    </aside>
  )
}
